// src/hooks/useSectionOperations.js
import { useState, useCallback, useRef, useEffect } from 'react';
import useTaskStore from '../stores/taskStore';
import useConfirmDialog from './useConfirmationDialog';

/**
 * Custom hook for section operations with optimistic updates
 * 
 * @param {string} projectId - Project ID
 * @return {Object} Section operation handlers and state
 */
export const useSectionOperations = (projectId) => {
  // Removed realtime sync to prevent duplicate listeners
  const markOperationPending = () => {}; // Dummy function
  
  // Get store actions
  const {
    createSection,
    updateSection,
    deleteSection,
    reorderSections,
    fetchProjectSections,
    projectSections,
    isLoading: storeLoading
  } = useTaskStore();
  
  // Local state
  const [pendingOperations, setPendingOperations] = useState(new Set());
  const [localSections, setLocalSections] = useState([]);
  const [error, setError] = useState(null);
  
  // Dialog hook for confirmations
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Track if sections are initialized from store
  const sectionsInitialized = useRef(false);
  const apiInProgress = useRef({});
  useEffect(() => {
    return () => {
      if (apiInProgress.current.tasks) {
        apiInProgress.current.tasks = false;
      }
    };
  }, []);
  // Load sections from store when they change
  useEffect(() => {
    const storeSections = projectSections[projectId] || [];
    
    if (storeSections.length > 0 && !sectionsInitialized.current) {
      sectionsInitialized.current = true;
      setLocalSections(storeSections);
    }
    
    return () => {
      if (storeSections.length === 0) {
        sectionsInitialized.current = false;
      }
    };
  }, [projectId, projectSections]);
  
  // Update local sections when store sections change
  useEffect(() => {
    const storeSections = projectSections[projectId] || [];
    
    // Only update local state if we have pending operations or if store data has changed
    if (storeSections.length > 0 && (pendingOperations.has('section') || 
        JSON.stringify(localSections) !== JSON.stringify(storeSections))) {
      setLocalSections(storeSections);
      
      // Clear pending operation flag if needed
      if (pendingOperations.has('section')) {
        setPendingOperations(prev => {
          const newSet = new Set(prev);
          newSet.delete('section');
          return newSet;
        });
      }
    }
  }, [projectId, projectSections, pendingOperations]); // Removed localSections to prevent loops
  
  /**
   * Load sections from the API
   */
  const loadSections = useCallback(() => {
    console.log('🔍 loadSections called with projectId:', projectId);
    
    if (!projectId) {
      console.log('🔍 No projectId, returning empty array');
      return Promise.resolve([]);
    }
    
    // Skip if this API call is already in progress
    if (apiInProgress.current.sections) {
      console.log("🔍 Sections API call already in progress, skipping");
      return Promise.resolve([]);
    }
    
    console.log('🔍 Calling fetchProjectSections...');
    
    // Mark API call as in progress
    apiInProgress.current.sections = true;
    setError(null);
    
    return fetchProjectSections(projectId)
      .then(sections => {
        console.log('🔍 fetchProjectSections completed, sections:', sections);
        
        // Mark API call as complete
        apiInProgress.current.sections = false;
        
        // If we got sections, update local state
        if (sections) {
          console.log('🔍 Setting local sections:', sections);
          setLocalSections(sections);
        }
        
        // Clear any pending section operations
        setPendingOperations(prev => {
          const newSet = new Set(prev);
          newSet.delete('section');
          return newSet;
        });
        
        return sections || [];
      })
      .catch(err => {
        // Always mark as complete, even on error
        apiInProgress.current.sections = false;
        setError(err.message || 'Failed to load sections');
        
        return Promise.reject(err);
      });
  }, [projectId, fetchProjectSections]);
  
  /**
   * Create a new section with optimistic update
   */
  const handleCreateSection = useCallback(async (sectionName) => {
    console.log('sectionName', sectionName);
    sectionName = sectionName?.name || sectionName;
    if (!sectionName || !sectionName?.trim() ) {
      console.log("Cannot create section - no name provided");
      return false;
    }
    
    // Update pending operations BEFORE creating temp section
    setPendingOperations(prev => {
      const newSet = new Set(prev);
      newSet.add('section');
      return newSet;
    });
    
    // Generate temporary ID for optimistic update
    const tempId = `temp-section-${Date.now()}`;
    
    // Create a temporary section
    const tempSection = {
      _id: tempId,
      name: sectionName?.trim(),
      orderIndex: localSections.length,
      collapsed: false,
      isTemp: true // Flag to identify optimistically created sections
    };
    
    // Only then update local sections
    setLocalSections(prevSections => [...prevSections, tempSection]);
    
    try {
      const sections = projectSections[projectId] || [];
      const orderIndex = sections.length;
      
      const result = await createSection(projectId, {
        name: sectionName?.trim(),
        orderIndex
      });
      
      // Mark operation as pending for the new section
      if (result?._id) {
        markOperationPending('section:create', result._id);
      }
      
      // Refresh sections to get the real section data
      await loadSections();
      
      // Clear pending operation
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete('section');
        return newSet;
      });
      
      return true;
    } catch (error) {
      console.log('Failed to create section:', error);
      
      // Remove temp section
      setLocalSections(prevSections => prevSections.filter(section => section._id !== tempId));
      
      // Clear pending operation
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete('section');
        return newSet;
      });
      
      setError(error.message || 'Failed to create section');
      return false;
    }
  }, [createSection, projectId, projectSections, loadSections, localSections.length, markOperationPending]);
  
  /**
   * Update a section with optimistic update
   */
  const handleSectionRename = useCallback(async (sectionId, newName) => {
    if (!sectionId || !newName || !newName.trim()) {
      console.log("Cannot rename section - invalid data provided");
      return false;
    }
    
    // Optimistic update: Update section name locally
    setLocalSections(prevSections => 
      prevSections.map(section => 
        section._id === sectionId ? { ...section, name: newName.trim() } : section
      )
    );
    
    // Mark section operations as pending
    setPendingOperations(prev => new Set(prev).add('section'));
    
    try {
      markOperationPending('section:update', sectionId);
      await updateSection(sectionId, { name: newName.trim() });
      
      // Refresh sections to ensure consistency
      await loadSections();
      
      // Clear pending operation
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete('section');
        return newSet;
      });
      
      return true;
    } catch (error) {
      console.log('Failed to rename section:', error);
      
      // Revert optimistic update
      await loadSections();
      
      // Clear pending operation
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete('section');
        return newSet;
      });
      
      setError(error.message || 'Failed to rename section');
      return false;
    }
  }, [updateSection, loadSections, markOperationPending]);
  
  /**
   * Delete a section with confirmation and optimistic update
   */
  const handleSectionDelete = useCallback(async (sectionId) => {
    if (!sectionId) {
      console.log("Cannot delete section - no sectionId provided");
      return false;
    }
    
    const confirmed = await confirm({
      title: 'Delete Section',
      content: 'Are you sure you want to delete this section? Tasks in this section will not be deleted but will be moved to "Draft".',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (!confirmed) return false;
    
    // Optimistic update: Remove section from local sections
    setLocalSections(prevSections => prevSections.filter(section => section._id !== sectionId));
    
    // Mark operations as pending
    setPendingOperations(prev => {
      const newSet = new Set(prev);
      newSet.add('section');
      return newSet;
    });
    
    try {
      markOperationPending('section:delete', sectionId);
      await deleteSection(sectionId);
      
      // Refresh data to ensure consistency
      await loadSections();
      
      // Clear pending operations
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete('section');
        return newSet;
      });
      
      return true;
    } catch (error) {
      console.log('Failed to delete section:', error);
      
      // Revert optimistic updates
      await loadSections();
      
      // Clear pending operations
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete('section');
        return newSet;
      });
      
      setError(error.message || 'Failed to delete section');
      return false;
    }
  }, [confirm, deleteSection, loadSections, markOperationPending]);
  
  /**
   * Reorder sections with optimistic update
   */
  const handleReorderSections = useCallback(async (orderedSectionIds) => {
    // Verify we have these sections
    const currentSections = projectSections[projectId] || [];
    if (currentSections.length === 0 ||
        orderedSectionIds.length !== currentSections.length) {
      return Promise.reject(new Error('Invalid section order'));
    }

    // Optimistic update: reorder sections
    setLocalSections(state => {
      // Create a map of id -> section for easy lookup
      const sectionsMap = {};
      currentSections.forEach(section => {
        sectionsMap[section._id] = section;
      });

      // Create reordered array with updated orderIndex values
      return orderedSectionIds.map((id, index) => ({
        ...sectionsMap[id],
        orderIndex: index
      }));
    });

    // Mark section operations as pending
    setPendingOperations(prev => new Set(prev).add('section'));

    try {
      await reorderSections(projectId, orderedSectionIds);
      
      // Clear pending operation
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete('section');
        return newSet;
      });
      
      return true;
    } catch (error) {
      console.log('Failed to reorder sections:', error);
      
      // Revert optimistic update
      await loadSections();
      
      // Clear pending operation
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete('section');
        return newSet;
      });
      
      setError(error.message || 'Failed to reorder sections');
      return false;
    }
  }, [projectId, projectSections, reorderSections, loadSections]);
  
  /**
   * Clear any errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  return {
    // State
    localSections,
    isLoading: storeLoading || pendingOperations.size > 0,
    pendingOperations: Array.from(pendingOperations),
    hasPendingOperations: pendingOperations.size > 0,
    error,
    
    // Section operations
    loadSections,
    handleCreateSection,
    handleSectionRename,
    handleSectionDelete,
    handleReorderSections,
    clearError,
    
    // Confirmation dialog component
    ConfirmDialog
  };
};